import VisuallyHidden from '@reach/visually-hidden'
import React from 'react'

import ExternalLink from '@helios/shared/components/external-link'
import LocaleSwitcher from '@helios/shared/components/locale-switcher'
import PrismicLink, {Props as PrismicLinkProps} from '@helios/shared/components/prismic-link'
import RichText, {Props as RichTextProps} from '@helios/shared/components/rich-text'
import logoFooterIcon from '@helios/shared/images/icons/logo-horizontal.svg'
import {socialLinks} from '@helios/shared/site-config'
import {Text, TextLink, NavTextLink} from '@helios/shared/styles/typography'
import Locale from '@helios/shared/types/locale'
import {showIntercom} from '@helios/shared/utils/intercom'

import * as s from './footer.styles'

type FooterProps = {
    locales?: Locale[]
    content: {
        entity: string
        address: RichTextProps['content']
        cvr: string
        terms_body: string
        copyright_text: string
        contact_us: string
        info_email: string
        support_email: string
        product_heading: string
        resources_heading: string
        company_heading: string
        legal_heading: string
        product_links: Link[]
        resources_links: Link[]
        company_links: Link[]
        legal_links: Link[]
    }
}

export const Footer: React.FC<FooterProps> = ({content, locales}) => {
    return (
        <s.Wrapper>
            <s.Details>
                <s.Logo src={logoFooterIcon} alt="" />

                <Text as={s.Address} color="muted">
                    <span>
                        {content.entity}
                        <br />
                    </span>

                    <RichText content={content.address} />

                    <span>CVR: {content.cvr}</span>
                </Text>

                <LocaleSwitcher locales={locales} light />

                <s.SocialLinks>
                    {socialLinks.map((link) => (
                        <ExternalLink key={link.name} to={link.url}>
                            <s.SocialIcon as={link.icon} width={24} height={24} aria-hidden />

                            <VisuallyHidden>{link.name}</VisuallyHidden>
                        </ExternalLink>
                    ))}
                </s.SocialLinks>

                <s.LinkGroup>
                    <TextLink bgColor="black" as="button" onClick={showIntercom}>
                        {content.contact_us}
                    </TextLink>
                    <TextLink bgColor="black" as="a" href={`mailto:${content.info_email}`}>
                        {content.info_email}
                    </TextLink>
                    <TextLink bgColor="black" as="a" href={`mailto:${content.support_email}`}>
                        {content.support_email}
                    </TextLink>
                </s.LinkGroup>
            </s.Details>

            <s.Links>
                <LinkGroup heading={content.product_heading} links={content.product_links} />
                <LinkGroup heading={content.resources_heading} links={content.resources_links} />
                <LinkGroup heading={content.company_heading} links={content.company_links} />
                <LinkGroup heading={content.legal_heading} links={content.legal_links} />

                <s.Terms>
                    <Text color="muted" size="xs" font="secondary">
                        {content.terms_body}
                    </Text>

                    <Text color="muted" size="xs" font="secondary">
                        {content.entity}, {new Date().getFullYear()}. {content.copyright_text}
                    </Text>
                </s.Terms>
            </s.Links>
        </s.Wrapper>
    )
}

type Link = {
    text: string
    link: PrismicLinkProps
}

type LinkGroupProps = {
    heading: string
    links: Link[]
}

const LinkGroup: React.FC<LinkGroupProps> = ({heading, links}) => (
    <s.LinkGroup>
        <s.SectionHeading>
            <Text as="h3" size="xs" color="muted" upperCase font="secondary">
                {heading}
            </Text>
        </s.SectionHeading>

        {links?.map((item, i) => (
            <NavTextLink as={PrismicLink} key={i} {...item?.link}>
                {item.text}
            </NavTextLink>
        ))}
    </s.LinkGroup>
)
